import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Images, Loader2 } from "lucide-react";
import type { ImageSource } from "@app/shared";
import { SETTING_KEYS } from "@app/shared";
import { api, type SettingDTO } from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useToast } from "../components/Toast";

/** Resolve the global default hero-image source; defaults to "pexels". */
function resolveImageSourceDefault(
  settings: SettingDTO[] | undefined,
): ImageSource {
  const found = settings?.find((s) => s.key === SETTING_KEYS.imageSource);
  return found?.value === "library" ? "library" : "pexels";
}

/**
 * Global (app-wide) settings — admin only. Anything PER-BRAND (Instagram,
 * WordPress, cadence, article/SEO, AI guide, logo, login) lives on the Brand
 * pages (⚙ → Kelola Brand → Konfigurasi).
 */
export function Settings() {
  const settingsQuery = useQuery({
    queryKey: ["settings"],
    queryFn: ({ signal }) => api.settings(signal),
  });
  const imageSourceDefault = resolveImageSourceDefault(settingsQuery.data);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-navy">
          Pengaturan Global
        </h1>
        <p className="text-sm text-slate-500">
          Pengaturan tingkat aplikasi (berlaku untuk semua brand). Konfigurasi
          per-brand — Instagram, WordPress, cadence, AI guide — ada di{" "}
          <strong>Kelola Brand</strong>.
        </p>
      </div>

      <ImageSourceDefaultCard
        current={imageSourceDefault}
        loading={settingsQuery.isLoading}
      />
    </div>
  );
}

/**
 * Global default for the hero image of generated posts. Saved to the
 * `image_source` setting (admin-only on the API side). Generators with no
 * per-request override use this value; the per-generate selectors default to it.
 */
function ImageSourceDefaultCard({
  current,
  loading,
}: {
  current: ImageSource;
  loading: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState<ImageSource>(current);

  // Sync the local control when the stored value loads/changes.
  useEffect(() => setValue(current), [current]);

  const save = useMutation({
    mutationFn: (v: ImageSource) =>
      api.updateSetting(SETTING_KEYS.imageSource, v),
    onSuccess: (_data, v) => {
      setValue(v);
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
      void queryClient.invalidateQueries({ queryKey: ["imageSourceDefault"] });
      toast.success(
        v === "library"
          ? "Default sumber gambar: Galeri"
          : "Default sumber gambar: Pexels",
      );
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal menyimpan"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Images className="h-4 w-4 text-teal" />
          Sumber gambar default
        </CardTitle>
        <CardDescription>
          Sumber gambar utama saat generate konten. <strong>Pexels</strong>{" "}
          mengambil foto stok; <strong>Galeri</strong> membuat AI memilih gambar
          dari galeri unggahan Anda. Dipakai oleh generate otomatis (06:00) dan
          sebagai nilai awal di setiap form generate.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex max-w-xs items-center gap-3">
          <select
            aria-label="Sumber gambar default"
            value={value}
            disabled={loading || save.isPending}
            onChange={(e) => save.mutate(e.target.value as ImageSource)}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-1 disabled:opacity-50"
          >
            <option value="pexels">Pexels (stok foto) — default</option>
            <option value="library">Galeri (AI pilih dari unggahan)</option>
          </select>
          {save.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin text-teal" />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
